/**
 * API 工具函數
 * 統一處理 API 基底 URL 和租戶識別
 */

/**
 * 取得 API 基底 URL
 */
export function getApiBase(): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL;

  if (!apiBase) {
    throw new Error('API 位址未設定，請檢查環境變數 VITE_API_BASE_URL');
  }

  return apiBase;
}

/**
 * 從 URL 參數取得 tenant_id（單一網域架構）
 *
 * 單一網域架構下，所有租戶共用同一個網域，透過 URL query string 傳遞 tenantId。
 *
 * 支援的參數名稱：tenantId（優先）, tenant_id, rent_id, rentId, mock_tenant
 */
export function getTenantIdFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);

  // 依優先順序嘗試不同的參數名稱
  // 注意：mock_tenant 用於 Mock 模式，優先級較低
  const tenantId =
    urlParams.get('tenantId') ||
    urlParams.get('tenant_id') ||
    urlParams.get('rent_id') ||
    urlParams.get('rentId') ||
    urlParams.get('mock_tenant') ||
    urlParams.get('mock_tenantId');

  if (tenantId) {
    console.log(
      '[租戶識別] 從 URL 參數取得 tenantId（單一網域架構）:',
      tenantId
    );
    return tenantId.trim();
  }

  return null;
}

/**
 * 建立包含 X-Tenant-ID header 的 RequestInit 物件
 *
 * 單一網域架構策略：
 * - 所有 API 請求必須帶上 X-Tenant-ID header
 * - tenantId 從 URL query string 取得
 *
 * 後端識別優先順序：
 * 1. JWT Token（管理後台）
 * 2. X-Tenant-ID header（主要方式）✅
 * 3. 預設值 'default'（僅開發環境）
 */
export function getApiHeaders(
  tenantId?: string | null
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // 優先使用傳入的 tenantId，否則從 URL 取得
  const finalTenantId = tenantId || getTenantIdFromUrl();

  if (finalTenantId) {
    headers['X-Tenant-ID'] = finalTenantId;
    console.log('[API Headers] 加入 X-Tenant-ID:', finalTenantId);
  } else {
    // 警告：單一網域架構下，所有請求都應該帶上 tenantId
    console.warn('[API Headers] 警告：未找到 tenantId，API 請求可能失敗');
  }

  return headers;
}

/**
 * 從 Response 讀取 X-Request-ID 並儲存
 * 用於錯誤日誌追蹤
 */
export async function extractRequestIdFromResponse(
  response: Response
): Promise<void> {
  const requestId = response.headers.get('X-Request-ID');
  if (requestId) {
    const { setRequestId } = await import('../utils/requestId.js');
    setRequestId(requestId);
  }
}

/**
 * 為 Lite API 請求添加 groupId
 * 從 URL 或 sessionStorage 取得 groupId，並添加到 URL query 或 header
 */
export function addLiteGroupIdToRequest(
  url: string,
  options?: RequestInit
): { url: string; options: RequestInit } {
  // 動態導入以避免循環依賴
  let groupId: string | null = null;
  try {
    // 嘗試從 window.location 取得
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      groupId =
        urlParams.get('lineGroupId') || urlParams.get('groupId') || null;

      // 如果 URL 中沒有，嘗試從 sessionStorage 取得
      if (!groupId) {
        const backupGroupId = sessionStorage.getItem('lite_group_id_backup');
        if (backupGroupId) {
          groupId = backupGroupId;
        }
      }

      // 開發環境：如果還是沒有，使用 mock groupId
      if (
        !groupId &&
        (import.meta.env.MODE === 'development' || import.meta.env.DEV === true)
      ) {
        groupId =
          import.meta.env.VITE_MOCK_LINE_GROUP_ID ||
          'Ca56f94637cec434d27582291d19f510f';
        console.log('[Lite API] 🧪 開發環境：使用 mock groupId:', groupId);
      }
    }
  } catch (error) {
    console.warn('[Lite API] 取得 groupId 時發生錯誤:', error);
  }

  if (!groupId) {
    console.warn('[Lite API] 警告：未找到 groupId，API 請求可能失敗');
    return { url, options: options || {} };
  }

  // 將 groupId 添加到 URL query parameter
  try {
    const urlObj = new URL(url);
    if (!urlObj.searchParams.has('groupId')) {
      urlObj.searchParams.set('groupId', groupId);
    }
    url = urlObj.toString();
  } catch (_) {
    // 如果 URL 解析失敗（可能是相對路徑），手動添加
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}groupId=${encodeURIComponent(groupId)}`;
  }

  // 同時添加到 header（作為備份）
  const headers = new Headers(options?.headers);
  if (!headers.has('X-Lite-Group-ID') && !headers.has('x-lite-group-id')) {
    headers.set('X-Lite-Group-ID', groupId);
  }

  return {
    url,
    options: {
      ...options,
      headers,
    },
  };
}

/**
 * 統一的 fetch wrapper
 * 自動處理 X-Request-ID 的讀取和儲存
 */
export async function apiFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  const response = await fetch(url, options);

  // 從 Response Header 讀取 X-Request-ID 並儲存
  await extractRequestIdFromResponse(response);

  return response;
}

export interface LiteApiFetchOptions {
  /** Square 情境：傳入 6 碼 suffix，會加 X-Lite-Square-Suffix、不加 groupId */
  squareSuffix?: string;
}

/**
 * Lite API 專用的 fetch wrapper
 * 若傳入 squareSuffix 則加 X-Lite-Square-Suffix、不加 groupId；否則加 groupId
 */
export async function liteApiFetch(
  url: string,
  options?: RequestInit,
  liteOptions?: LiteApiFetchOptions
): Promise<Response> {
  if (liteOptions?.squareSuffix?.trim()) {
    const headers = new Headers(options?.headers);
    headers.set('X-Lite-Square-Suffix', liteOptions.squareSuffix.trim());
    return apiFetch(url, { ...options, headers });
  }
  const { url: finalUrl, options: finalOptions } = addLiteGroupIdToRequest(
    url,
    options
  );
  return apiFetch(finalUrl, finalOptions);
}
